import type { Ref } from 'vue';
import { CHANGED_FIELD, addChangedField, clearChangedFields, isFieldChanged } from '../lib/tourCriteriaCookie';
import { dateFromEqual, nightsEqual, resolveDefaultCriteria, type SearchCriteria } from '@/entities/searchCriteria';
import { normalizeDateFrom } from '@/features/searchForm/dateFrom';
import type { Selection } from '@/features/searchForm/destination';
import { normalizeRange } from '@/features/searchForm/nightsRange';
import type {
  Country,
  DateRange,
  DepartureCity,
  DepartureCityType,
  NightsRange,
  DefaultCriteriaIndex,
} from '@/shared/model';

export interface UseCrossFieldCriteriaOptions {
  countries: Ref<Country[]>;
  defaultCriteria: Ref<DefaultCriteriaIndex>;
  destination: Ref<Selection | null>;
  nights: Ref<NightsRange>;
  dateRange: Ref<DateRange | null>;
}

function cityHasType(type: DepartureCityType | undefined): boolean {
  return type === 'capital' || type === 'region';
}

function resetChangedFields(): void {
  clearChangedFields();
}

export function useCrossFieldCriteria(options: UseCrossFieldCriteriaOptions) {
  const { defaultCriteria, nights, dateRange } = options;

  const resolveNightsDefault = (defaultNights: NightsRange | undefined): NightsRange | null => {
    if (!defaultNights || isFieldChanged(CHANGED_FIELD.nights)) {
      return nights.value;
    }
    const next = normalizeRange(defaultNights);
    if (!nightsEqual(next, nights.value)) {
      return next;
    }
    return nights.value;
  };

  const resolveDateDefault = (
    type: DepartureCityType | undefined,
    defaultDate: DateRange | undefined,
  ): DateRange | null => {
    if (!cityHasType(type) || !defaultDate || isFieldChanged(CHANGED_FIELD.date)) {
      return dateRange.value;
    }
    const next = normalizeDateFrom(defaultDate);
    if (!dateFromEqual(next, dateRange.value)) {
      return next;
    }
    return dateRange.value;
  };

  const resolveDefaults = (city: DepartureCity | null, destination: Selection | null): Partial<SearchCriteria> => {
    const id = destination?.kind === 'none' ? null : destination?.country.id;
    if (!id) return {};
    const def = resolveDefaultCriteria(defaultCriteria.value, id, city?.type);

    return {
      nights: resolveNightsDefault(def.nights),
      dateRange: resolveDateDefault(city?.type, def.dateRange),
    };
  };

  const markTouchedNights = () => {
    addChangedField(CHANGED_FIELD.nights);
  };

  const markTouchedDate = () => {
    addChangedField(CHANGED_FIELD.date);
  };

  return {
    resolveDefaults,
    markTouchedNights,
    markTouchedDate,
    addChangedField,
    reset: resetChangedFields,
  };
}
